/**
 * VÉRAULT Member Suite — ExperienceLayout (Wave 26.21 Phase 2a).
 * ───────────────────────────────────────────────────────────────
 * Shared shell for every page INSIDE an Experience.
 *
 * Renders:
 *   ▸ Quiet breadcrumb (Your experiences / {Experience title})
 *   ▸ Sticky section pills nav (Overview · Itinerary · Essentials ·
 *     Travel Prep · Memories), horizontally scrollable on mobile.
 *   ▸ A loading skeleton, error empty state, and 404 fallback.
 *   ▸ The active section's content via React Router `<Outlet />`.
 *
 * Phase 2a discipline: NO business logic here. Just the shell.
 */
import React from "react";
import { Outlet, NavLink, useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, MessageSquare, ArrowUp } from "lucide-react";
import {
  Empty,
  Skeleton,
  PortalBreadcrumb,
  PortalButton,
  api,
  motion as portalMotion,
} from "../../../index";
import { deriveExperienceState, getStateLabel } from "../../../../hooks/useExperienceState";
import { useDestinationVeil } from "../../../../hooks/useDestinationVeil";
import useLiveSync from "../../../hooks/useLiveSync";
import NotificationsBell from "./NotificationsBell";

const { fadeInUp } = portalMotion;

const SECTIONS = [
  { key: "overview", label: "Overview", to: "" },
  { key: "itinerary", label: "Itinerary", to: "itinerary" },
  { key: "essentials", label: "Essentials", to: "essentials" },
  { key: "travel-prep", label: "Travel Prep", to: "travel-prep" },
  { key: "packing", label: "Packing", to: "packing" },
  { key: "memories", label: "Memories", to: "memories" },
];

// ─── Section pills nav ────────────────────────────────────────────
// Wave 30.4 — "Talk about this trip" relocates into the trailing edge
// of the SectionNav as a quiet utility chip. Clients emotionally
// ARRIVE on the eyebrow + hero first; the communication affordance
// lives one layer lower, never competing with the trip's identity.
function SectionNav({ tripId, onTalkAboutTrip, talkBusy }) {
  return (
    <motion.nav
      variants={fadeInUp}
      className="-mx-5 sm:mx-0 sticky top-[52px] z-20 bg-[hsl(var(--background))]/85 backdrop-blur-md border-b border-[hsl(var(--surface-hairline))]/40"
      data-testid="experience-section-nav"
      aria-label="Experience sections"
    >
      <div className="px-5 sm:px-0 py-3 flex items-center gap-1.5 overflow-x-auto no-scrollbar">
        {SECTIONS.map((s) => (
          <NavLink
            key={s.key}
            to={s.to ? `/portal/client/experiences/${tripId}/${s.to}` : `/portal/client/experiences/${tripId}`}
            end={s.to === ""}
            data-testid={`experience-nav-${s.key}`}
            className={({ isActive }) =>
              `shrink-0 px-3.5 py-1.5 rounded-full font-mono text-[10px] uppercase tracking-[0.22em] transition-colors whitespace-nowrap ${
                isActive
                  ? "bg-[hsl(var(--foreground))] text-[hsl(var(--background))]"
                  : "text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--background-paper))]/60"
              }`
            }
          >
            {s.label}
          </NavLink>
        ))}
        {/* Wave 30.4 — Quiet trailing utility: trip-thread affordance. */}
        {onTalkAboutTrip && (
          <button
            type="button"
            onClick={onTalkAboutTrip}
            disabled={talkBusy}
            className="ml-auto shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full font-mono text-[9.5px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--surface-hover))] transition-colors disabled:opacity-50"
            data-testid="experience-talk-about-trip"
            title="Open the trip thread with your advisor"
          >
            <MessageSquare className="size-3" strokeWidth={1.5} />
            <span className="hidden sm:inline">Talk about this trip</span>
            <span className="sm:hidden">Message</span>
          </button>
        )}
      </div>
    </motion.nav>
  );
}

import { useNavigate } from "react-router-dom";

// ─── Jump to top ──────────────────────────────────────────────────
// Floating utility that appears once the client has scrolled a good
// way down any section. Smooth-scrolls the window back to the top.
function JumpToTop() {
  const [show, setShow] = React.useState(false);
  React.useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 600);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <AnimatePresence>
      {show && (
        <motion.button
          key="jump-to-top"
          type="button"
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 14 }}
          transition={{ duration: 0.25, ease: [0.22, 0.61, 0.36, 1] }}
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          data-testid="experience-jump-to-top"
          aria-label="Jump to top"
          title="Jump to top"
          className="fixed bottom-6 right-5 sm:right-8 z-40 inline-flex items-center justify-center size-11 rounded-full bg-[hsl(var(--foreground))] text-[hsl(var(--background))] shadow-lg hover:bg-[hsl(var(--accent-gold))] transition-colors"
        >
          <ArrowUp className="size-4" strokeWidth={1.75} />
        </motion.button>
      )}
    </AnimatePresence>
  );
}

// ─── Header band: state chip + title (the cinematic hero lives in Overview itself) ──
// Wave 30.4 — "Talk about this trip" moved out of this row into the
// SectionNav trailing edge. The header now stays focused on emotional
// arrival (eyebrow + state chip) and stops competing visually with
// the hero.
function ExperienceHeaderRow({ trip }) {
  const { state } = deriveExperienceState(trip);
  const stateInfo = getStateLabel(state);
  const veil = useDestinationVeil(trip);
  const tripId = trip?.id;

  const toneClass = {
    gold: "bg-[hsl(var(--accent-gold))]/12 text-[hsl(var(--accent-gold))] border-[hsl(var(--accent-gold))]/35",
    sage: "bg-[hsl(168,22%,32%)]/10 text-[hsl(168,22%,32%)] border-[hsl(168,22%,32%)]/30",
    muted: "bg-[hsl(var(--background-paper))] text-[hsl(var(--foreground-muted))] border-[hsl(var(--surface-hairline))]/40",
  }[stateInfo.tone] || "bg-[hsl(var(--background-paper))] text-[hsl(var(--foreground-muted))] border-[hsl(var(--surface-hairline))]/40";

  return (
    <motion.div variants={fadeInUp} data-testid="experience-header-row" data-veiled={veil.veiled ? "true" : "false"} className="flex items-center justify-between gap-3 flex-wrap">
      <AnimatePresence initial={false} mode="wait">
        <motion.p
          key={veil.veiled ? "veiled-eyebrow" : "open-eyebrow"}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.6, ease: [0.22, 0.61, 0.36, 1] }}
          className="font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--foreground-muted))]"
        >
          {veil.veiled ? "Your secret experience" : "Your experience"}
        </motion.p>
      </AnimatePresence>
      <span
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border font-mono text-[10px] uppercase tracking-[0.14em] ${toneClass}`}
        data-testid="experience-state-chip"
        title={stateInfo.hint}
      >
        <span className={`size-1.5 rounded-full ${stateInfo.tone === "sage" ? "bg-[hsl(168,22%,32%)]" : stateInfo.tone === "gold" ? "bg-[hsl(var(--accent-gold))]" : "bg-[hsl(var(--foreground-muted))]"}`} />
        {stateInfo.label}
      </span>
      <NotificationsBell tripId={tripId} />
    </motion.div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────
export default function ExperienceLayout() {
  const { tripId } = useParams();
  const [trip, setTrip] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  // Wave 30 · Phase 7c — Single source of truth for live sync. Polls
  // /sync-state every 60s, visibility-aware, emits domain-specific
  // window events + Sonner toasts. Replaces the scattered per-page
  // polling that used to live in Essentials / TravelPrep / Memories.
  useLiveSync(tripId);

  // Trip-level fetcher (called on mount + whenever the live-sync hook
  // signals trip-domain change). Veil-lift cross-fade (Wave 26.22) still
  // works because the trip refetch picks up the new `reveal_destination`.
  React.useEffect(() => {
    if (!tripId) return undefined;
    let alive = true;
    setLoading(true);

    const fetchTrip = () =>
      api.get(`/client-portal/trips/${tripId}`)
        .then(({ data }) => { if (alive) { setTrip(data); setError(null); } })
        .catch((e) => {
          if (!alive) return;
          // Phase 1.4d · Context-aware empty states. 404 from the
          // client-portal endpoint specifically means "no trip for this
          // logged-in client" — which to the client should read as
          // "the experience is being prepared", not as a technical
          // failure. Other statuses (5xx, network) get the polished
          // transient-error wording.
          const status = e?.response?.status;
          if (status === 404 || status === 403) {
            setError({ kind: "pending", title: "Your experience is being curated.", subtitle: "Nothing has been published here yet." });
          } else {
            setError({ kind: "transient", title: "We couldn't open this experience.", subtitle: "Please return to your experiences or contact your advisor for help." });
          }
        });

    fetchTrip().finally(() => alive && setLoading(false));

    // Listen for live-sync trip changes (replaces 45s self-poll).
    const onTripSync = () => { if (document.visibilityState === "visible") fetchTrip(); };
    window.addEventListener("vt:sync-changed:trip", onTripSync);
    // Also refetch on tab focus for immediate catch-up.
    const onVis = () => {
      if (document.visibilityState === "visible") fetchTrip();
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      alive = false;
      window.removeEventListener("vt:sync-changed:trip", onTripSync);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [tripId]);

  // Compute the veil label up front (hook must always be called).
  const breadcrumbVeil = useDestinationVeil(trip);
  const navigate = useNavigate();
  const [talkBusy, setTalkBusy] = React.useState(false);

  // Wave 30.2 · Phase 8b — Trip-thread affordance (moved to SectionNav
  // trailing edge in Wave 30.4 so it no longer competes with the hero).
  const openTripThread = React.useCallback(async () => {
    if (!trip?.id) return;
    setTalkBusy(true);
    try {
      const { data } = await api.post(
        "/client-portal/concierge/conversations/ensure-trip-thread",
        { trip_id: trip.id },
      );
      navigate(`/portal/client/messages?thread=${data.id}`);
    } catch {
      navigate("/portal/client/messages");
    } finally {
      setTalkBusy(false);
    }
  }, [trip, navigate]);

  if (loading) {
    return (
      <div className="space-y-8" data-testid="experience-loading">
        <Skeleton className="h-5 w-48" />
        <Skeleton className="aspect-[21/9] w-full" />
      </div>
    );
  }
  if (error || !trip) {
    const e = (error && typeof error === "object") ? error : { title: "Your experience is being curated.", subtitle: "Nothing has been published here yet." };
    return (
      <Empty
        title={e.title}
        hint={e.subtitle}
        icon={<MapPin className="size-7" strokeWidth={1.25} />}
        action={
          <PortalButton as={Link} to="/portal/client/experiences" variant="ghost" size="sm">
            Back to your experiences
          </PortalButton>
        }
        testid="experience-error"
      />
    );
  }

  return (
    <motion.div
      initial="initial"
      animate="animate"
      variants={portalMotion.stagger}
      className="space-y-7 sm:space-y-9"
      data-testid="experience-root"
      data-experience-state={deriveExperienceState(trip).state}
    >
      <motion.div variants={fadeInUp}>
        <PortalBreadcrumb
          parent={{ to: "/portal/client/experiences", label: "Your experiences" }}
          trail={[{ label: breadcrumbVeil.label }]}
          exitTo="/"
          exitLabel="Exit to website"
        />
      </motion.div>

      <ExperienceHeaderRow trip={trip} />

      {/* Wave 30.4 — extra breathing room between the utility/header
          stack and the tabs. Luxury UI requires staged arrival. */}
      <div className="pt-3 sm:pt-5" />

      <SectionNav tripId={tripId} onTalkAboutTrip={openTripThread} talkBusy={talkBusy} />

      {/* Outlet receives the experience via context so child sections
          don't have to refetch the trip. */}
      <Outlet context={{ trip }} />

      <JumpToTop />
    </motion.div>
  );
}
